import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { Pencil } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import TemporaryDrawer from '@/components/TemporaryDrawer';
import { useConfirm } from '@/hooks/useConfirm';

import { maskAddressValue } from '../../../common/utils/format';
import { tiersQueryKeys, useTiersQuery } from '../../tiers/hooks/useTiersQuery';
import { tierUsersQueryKeys } from '../../tiers/hooks/useTierUsersQuery';
import { tierApprovalsService } from '../../tiers/infra/tierApprovalsService';
import { type AffiliateDetail, type AffiliateStatus, REGION_OPTIONS } from '../domain/types';
import { useClearTierProtection } from '../hooks/useClearTierProtection';
import { useSetTierProtection } from '../hooks/useSetTierProtection';
import { useUpdateAffiliate } from '../hooks/useUpdateAffiliate';
import { AffiliateForm, type AffiliateFormValues } from './AffiliateForm';
import { type PendingProtection } from './TierProtectionCard';

interface EditAffiliateModalProps {
  open: boolean;
  onClose: () => void;
  affiliate: AffiliateDetail;
  projectId: string;
}

const validationSchema = Yup.object({
  alias: Yup.string().optional(),
  region: Yup.string()
    .oneOf([...REGION_OPTIONS, ''], 'Invalid region')
    .optional(),
  status: Yup.string().required('Status is required'),
  note: Yup.string().optional(),
});

export function EditAffiliateModal({ open, onClose, affiliate, projectId }: EditAffiliateModalProps) {
  const { updateAffiliateAsync, isLoading } = useUpdateAffiliate();
  const { setTierProtection, isLoading: isSettingProtection } = useSetTierProtection();
  const { clearTierProtection, isLoading: isClearingProtection } = useClearTierProtection();
  const { data: tiers = [] } = useTiersQuery();
  const sortedTiers = useMemo(() => [...tiers].sort((a, b) => a.rank - b.rank), [tiers]);
  const queryClient = useQueryClient();

  const [pendingProtection, setPendingProtection] = useState<PendingProtection | null>(null);
  const [pendingClear, setPendingClear] = useState(false);
  const [protectionIncomplete, setProtectionIncomplete] = useState(false);
  const [ConfirmDialog, confirm] = useConfirm('warning');

  const handlePendingChange = useCallback(
    (pending: PendingProtection | null, clearing: boolean, incomplete: boolean) => {
      setPendingProtection(pending);
      setPendingClear(clearing);
      setProtectionIncomplete(incomplete);
    },
    [],
  );

  const formik = useFormik<AffiliateFormValues>({
    initialValues: {
      alias: affiliate.alias ?? '',
      region: affiliate.region,
      status: affiliate.status,
      note: affiliate.note ?? '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (pendingClear) {
        const confirmed = await confirm({
          title: 'Remove tier protection',
          message:
            "Removing the protection will re-evaluate the affiliate's tier based on audience conditions. This may result in a tier change.",
          confirmButtonText: 'Remove protection',
          cancelButtonText: 'Cancel',
        });
        if (!confirmed) return;
      }

      const pendingTier = pendingProtection ? sortedTiers.find((t) => t.id === pendingProtection.tier_id) : null;
      const requiresApproval = !!pendingTier?.requiresManualProjectApproval;

      if (requiresApproval && pendingTier) {
        const confirmed = await confirm({
          title: 'Approve and assign tier protection',
          message: `${pendingTier.name} requires manual approval. Saving will assign the affiliate to this tier and approve them automatically. Continue?`,
          confirmButtonText: 'Approve and save',
          cancelButtonText: 'Cancel',
        });
        if (!confirmed) return;
      }

      await updateAffiliateAsync({
        projectId,
        affiliateId: affiliate.id,
        body: {
          alias: values.alias || undefined,
          region: values.region as typeof affiliate.region,
          status: values.status as AffiliateStatus,
          note: values.note || undefined,
        },
      });

      if (pendingClear) {
        await clearTierProtection({ projectId, affiliateId: affiliate.id });
      } else if (pendingProtection) {
        // If changing tier on an existing protection, clear the old one first
        const hasExistingWithDifferentTier = affiliate.protections?.some(
          (p) => p.tier_id !== pendingProtection.tier_id,
        );
        if (hasExistingWithDifferentTier) {
          await clearTierProtection({ projectId, affiliateId: affiliate.id });
        }
        const existingProtection = affiliate.protections?.[0];
        let expiresAt: Date;
        if (pendingProtection.isExtension && existingProtection) {
          // Extend from current ends_at
          expiresAt = new Date(existingProtection.ends_at);
          expiresAt.setDate(expiresAt.getDate() + pendingProtection.days);
        } else {
          // New protection: days from created_at or today
          const baseDate = existingProtection?.created_at ? new Date(existingProtection.created_at) : new Date();
          expiresAt = new Date(baseDate);
          expiresAt.setDate(expiresAt.getDate() + pendingProtection.days);
        }
        await setTierProtection({
          projectId,
          affiliateId: affiliate.id,
          body: { tier_id: pendingProtection.tier_id, expires_at: expiresAt.toISOString() },
        });

        if (requiresApproval) {
          try {
            await tierApprovalsService.approve(projectId, {
              project_tier_id: pendingProtection.tier_id,
              user_identifier: affiliate.user_identifier,
              user_identifier_type: affiliate.user_identifier_type,
            });
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: tiersQueryKeys.all(projectId) }),
              queryClient.invalidateQueries({ queryKey: tierUsersQueryKeys.all(projectId, pendingProtection.tier_id) }),
              queryClient.invalidateQueries({ queryKey: ['affiliate-management', 'list'] }),
              queryClient.invalidateQueries({ queryKey: ['affiliate-management', 'detail', projectId, affiliate.id] }),
              queryClient.invalidateQueries({ queryKey: ['project-affiliates'] }),
            ]);
          } catch {
            toast.error('Protection saved, but approval failed. You can approve from the tier page.');
          }
        }
      }

      onClose();
    },
  });

  const handleClose = () => {
    setPendingProtection(null);
    setPendingClear(false);
    onClose();
  };

  const hasPendingChanges = formik.dirty || pendingProtection !== null || pendingClear;
  const submitDisabled = !hasPendingChanges || protectionIncomplete;

  return (
    <TemporaryDrawer
      open={open}
      onClose={handleClose}
      title="Edit Affiliate"
      subtitle={affiliate.alias || maskAddressValue(affiliate.user_identifier)}
      icon={<Pencil size={20} />}
      shouldConfirmClose={hasPendingChanges}
    >
      <AffiliateForm
        mode="edit"
        projectId={projectId}
        formik={formik}
        identifier={affiliate.user_identifier}
        identifierType={affiliate.user_identifier_type}
        organicTier={affiliate.organic_tier}
        currentTier={affiliate.current_tier}
        protections={affiliate.protections ?? []}
        tiers={sortedTiers}
        onPendingProtectionChange={handlePendingChange}
        onCancel={handleClose}
        submitLabel="Save Changes"
        submittingLabel="Saving..."
        isSubmitting={isLoading}
        isProtectionLoading={isSettingProtection || isClearingProtection}
        submitDisabled={submitDisabled}
      />
      {ConfirmDialog}
    </TemporaryDrawer>
  );
}
